import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';

const pulseLogo = keyframes`
  0% {
    transform: scale(0.9);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.5;
  }
`;

const rotateRing = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

/**
 * BrandLoader Component
 * Renders a premium, brand-authentic loading animation featuring:
 * 1. A pulsing Readable English logo adapting to dark/light theme filters
 * 2. Alternating rotating dual accent rings (Teal and Peach/Salmon)
 * 3. An optional pulsing status message
 */
const BrandLoader = ({ size = 100, message = '' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 4
      }}
    >
      <Box 
        sx={{ 
          position: 'relative', 
          width: size, 
          height: size, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        {/* Outer Rotating Segmented Ring */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.primary.main,
            borderBottomColor: theme.palette.secondary.main,
            animation: `${rotateRing} 1.5s linear infinite`,
          }}
        />
        {/* Inner Reverse-Rotating Segmented Ring */}
        <Box
          sx={{
            position: 'absolute',
            width: '78%',
            height: '78%',
            borderRadius: '50%',
            border: '2px solid transparent',
            borderLeftColor: theme.palette.success.main,
            borderRightColor: theme.palette.secondary.light,
            animation: `${rotateRing} 1s linear infinite reverse`,
          }}
        />
        {/* Pulsing Brand Logo Image */}
        <Box
          component="img"
          src="https://readablenglish.com/hs-fs/hubfs/Readable%20English%20Logo%20(1).png"
          alt="Readable English Loading"
          sx={{
            width: '52%',
            height: '52%',
            objectFit: 'contain',
            filter: isDark ? 'none' : 'invert(1) hue-rotate(180deg)',
            animation: `${pulseLogo} 2s ease-in-out infinite`,
          }}
        />
      </Box>
      {message && (
        <Typography
          variant="body2"
          sx={{
            mt: 3,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            animation: `${pulseLogo} 2s ease-in-out infinite`,
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default BrandLoader;
